import { Injectable } from '@angular/core';
import { DataService } from '../data.service';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

/**
 *  NAV-NYOMTATVÁNYMOTOR – kliens (backend/nav-nyomtatvany.js) és az azonnali mezőellenőrzés.
 *  A modell a NAV XSD-jéből készül (backend/nav-nyomtatvany-modell.js); a mező-szabályok itt
 *  UGYANAZOK, mint a kiszolgálón (mezoHibak) – a kiszolgáló mentéskor újra ellenőriz.
 */
export interface NyMezo {
  tipus: 'mezo'; nev: string; kod: string; cim: string; kotelezo?: boolean;
  sor?: string; sorCim?: string; oszlop?: string; oszlopCim?: string; rovid?: string; technev?: string;
  /** „Székhelye ¦ Ország”: az alcsoport (előtag); `_alfej` a csoport első mezőjén (a kitöltő tölti). */
  elotag?: string; _alfej?: string;
  adattipus: 'szoveg' | 'szam' | 'datum' | 'logikai' | 'kodlista';
  /** Az XSD-ben szabad szövegű mezőhöz az ÁNYK-sablon listája – JAVASLAT (nem kötelező érvény). */
  javasoltErtekek?: { ertek: string; cim: string | null }[];
  ertekek?: { ertek: string; cim: string | null }[]; minHossz?: number; maxHossz?: number; mintak?: string[];
  min?: number; max?: number; kulsoTipus?: string;
}
export interface NyCsomopont {
  tipus: 'form' | 'blokk' | 'csoport' | 'lanc'; nev: string; cim: string; min: number; max: number | null; elemek: (NyCsomopont | NyMezo)[];
  lap?: string | null; kod?: string;
  /** Lánc: a sorok (Chain_elem) előfordulása, a fejléc (Chain_header) tartalma, a rögzített attribútumok (sdgID). */
  elemMin?: number; elemMax?: number | null; fej?: (NyCsomopont | NyMezo)[] | null; attrs?: Record<string, string>;
}
export interface NyModell { kod: string; nev: string; namespace: string; verzio: string; gyoker: string; formok: NyCsomopont[]; mezoDb: number; }
/** A fejlesztői nyomtatványszerkesztőben igazított megjelenés (az XSD-szerkezet nem változik). */
export interface NyMezoElrendezes { cim?: string; sugo?: string; doboz?: number; szeles?: 'kicsi' | 'kozepes' | 'szeles' | 'teljes'; }
export interface NyElrendezes { mezok?: Record<string, NyMezoElrendezes>; blokkok?: Record<string, { cim?: string }>;
  /** Mezőcsoportonként a mezők megjelenítési sorrendje (az XML sorrendjét nem érinti). */
  sorrend?: Record<string, string[]>; }
/** `ut`: az adatobjektum útja a példányon belül – '' a lapon, 'Chain_X[0]/Chain_Y[1]' láncsorban, 'Chain_X#fej' fejlécben. */
export interface NyHiba {
  form: string; peldany: number; ut: string; mezo: string | null; cim: string; lap: string; uzenet: string;
  /** A hiba helye a nyomtatványképen: az ÁNYK-mezőazonosító, a lap neve és dinamikus lapnál a példány indexe. */
  kod?: string | null; szint?: string; fid?: string; lapIndex?: number; anykLap?: string;
}

@Injectable({ providedIn: 'root' })
export class NavNyomtatvanyService {
  private readonly base = '/api/nav-nyomtatvany';
  constructor(private http: HttpClient, private data: DataService) {}

  /**  A tárhely-igazolások párosítása (backend/nav-igazolas.js) – saját útvonalon fut. */
  readonly igazolas = {
    get: <T = any>(p: string) => firstValueFrom(this.http.get<T>('/api/nav-igazolas' + p)),
    post: <T = any>(p: string, b: any) => firstValueFrom(this.http.post<T>('/api/nav-igazolas' + p, b)),
    put: <T = any>(p: string, b: any) => firstValueFrom(this.http.put<T>('/api/nav-igazolas' + p, b)),
  };
  /**  Fájl megnyitása a kiszolgálóról – tokennel (a link nem visz jegyet). */
  fajlMegnyit(url: string, cim = 'Dokumentum') { return this.data.megnyitFajl(url, cim); }

  get<T = any>(p: string) { return firstValueFrom(this.http.get<T>(this.base + p)); }
  post<T = any>(p: string, b: any) { return firstValueFrom(this.http.post<T>(this.base + p, b)); }
  put<T = any>(p: string, b: any) { return firstValueFrom(this.http.put<T>(this.base + p, b)); }
  del<T = any>(p: string) { return firstValueFrom(this.http.delete<T>(this.base + p)); }
  xml(id: string, eroltetett = false) {
    return firstValueFrom(this.http.get(`${this.base}/bevallas/${id}/xml${eroltetett ? '?eroltetett=1' : ''}`, { responseType: 'text' }));
  }

  /* ── ellenőrzés (a kiszolgáló nav-nyomtatvany-modell.js párja) ── */
  private regexTar = new Map<string, RegExp | null>();
  private regex(minta: string): RegExp | null {
    if (this.regexTar.has(minta)) return this.regexTar.get(minta)!;
    let r: RegExp | null = null;
    try { r = new RegExp('^(?:' + minta.replace(/\\i/g, '[A-Za-z_:]').replace(/\\c/g, '[A-Za-z0-9._:-]') + ')$', 'u'); } catch { r = null; }
    this.regexTar.set(minta, r);
    return r;
  }
  ures(f: NyMezo, v: any): boolean { return v === undefined || v === null || v === '' || (v === false && f.adattipus !== 'logikai'); }

  mezoHibak(f: NyMezo, v: any): string[] {
    const h: string[] = [];
    if (this.ures(f, v)) return h;
    const s = String(v);
    if (f.adattipus === 'logikai') return h;
    if (f.adattipus === 'kodlista' && f.ertekek && !f.ertekek.some((e) => e.ertek === s)) h.push('Érvénytelen érték.');
    if (f.minHossz !== undefined && s.length < f.minHossz) h.push(`Legalább ${f.minHossz} karakter.`);
    if (f.maxHossz !== undefined && s.length > f.maxHossz) h.push(`Legfeljebb ${f.maxHossz} karakter.`);
    if (f.mintak?.length && !f.mintak.some((m) => { const r = this.regex(m); return !r || r.test(s); })) h.push(f.adattipus === 'szam' ? 'Csak számjegyek.' : `Formátum: ${f.mintak[0]}`);
    if (f.adattipus === 'datum' && !/^\d{4}-\d{2}-\d{2}$/.test(s)) h.push('Dátum: ÉÉÉÉ-HH-NN.');
    if (f.min !== undefined && Number(s) < f.min) h.push(`Legalább ${f.min}.`);
    if (f.max !== undefined && Number(s) > f.max) h.push(`Legfeljebb ${f.max}.`);
    return h;
  }

  lancSorai(n: any, ad: any): any[] { return Array.isArray(ad?.[n.nev]) ? ad[n.nev] : []; }
  lancFeje(n: any, ad: any): any { return ad && ad[n.nev + '#fej'] && typeof ad[n.nev + '#fej'] === 'object' ? ad[n.nev + '#fej'] : {}; }
  vanErtek(n: any, ad: any): boolean {
    if (!ad) return false;
    if (n.tipus === 'mezo') return !this.ures(n, ad[n.kod]);
    if (n.tipus === 'lanc') return this.lancSorai(n, ad).some((s: any) => n.elemek.some((e: any) => this.vanErtek(e, s)))
      || (n.fej || []).some((e: any) => this.vanErtek(e, this.lancFeje(n, ad)));
    return (n.elemek || []).some((e: any) => this.vanErtek(e, ad));
  }

  /** A kiszolgáló ellenoriz() párja (nav-nyomtatvany-modell.js) – ugyanazokkal az utakkal. */
  ellenoriz(m: NyModell, adat: any): NyHiba[] {
    const hibak: NyHiba[] = [];
    for (const form of m.formok) {
      const peld: any[] = adat?.[form.nev] || [];
      if (form.min >= 1 && !peld.length) hibak.push({ form: form.nev, peldany: 0, ut: '', mezo: null, cim: form.cim, lap: form.kod || '', uzenet: `A(z) ${form.kod} részbizonylat kötelező.` });
      peld.forEach((p, pi) => {
        const hiba = (lap: string, ut: string, n: any, uzenet: string) => hibak.push({ form: form.nev, peldany: pi, ut, mezo: n.tipus === 'mezo' ? n.kod : null, cim: n.cim, lap, uzenet });
        const bejar = (n: any, kovetelt: boolean, lap: string, ad: any, ut: string) => {
          if (n.tipus === 'mezo') {
            const v = ad[n.kod];
            if (this.ures(n, v) && n.kotelezo && kovetelt) hiba(lap, ut, n, 'Kötelező mező.');
            for (const u of this.mezoHibak(n, v)) hiba(lap, ut, n, u);
            return;
          }
          if (n.tipus === 'lanc') {
            const sorok = this.lancSorai(n, ad);
            if (n.elemMax != null && sorok.length > n.elemMax) hiba(lap, ut, n, `Legfeljebb ${n.elemMax} sor lehet.`);
            const fejUt = (ut ? ut + '/' : '') + n.nev + '#fej';
            (n.fej || []).forEach((e: any) => bejar(e, this.vanErtek(e, this.lancFeje(n, ad)), lap, this.lancFeje(n, ad), fejUt));
            sorok.forEach((sor: any, si: number) => {
              const van = n.elemek.some((e: any) => this.vanErtek(e, sor));
              n.elemek.forEach((e: any) => bejar(e, van, lap, sor, `${ut ? ut + '/' : ''}${n.nev}[${si}]`));
            });
            return;
          }
          const lapKod = n.tipus === 'blokk' ? (n.lap || n.cim) : lap;
          const kov = (n.min >= 1 && kovetelt) || this.vanErtek(n, ad);
          (n.elemek || []).forEach((e: any) => bejar(e, kov, lapKod, ad, ut));
        };
        (form.elemek || []).forEach((b) => bejar(b, true, form.kod || '', p || {}, ''));
      });
    }
    return hibak;
  }
}
